import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function CustomDomain() {
  const { profile } = useAppContext();
  const [domain, setDomain] = useState(null);
  const [inputDomain, setInputDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [error, setError] = useState(null);

  const isPro = profile?.subscription_status === 'active' || profile?.is_lifetime;

  useEffect(() => {
    fetchDomain();
  }, [profile]);

  const fetchDomain = async () => {
    if (!profile) return;
    try {
      const { domain: data } = await api.get('domain');
      setDomain(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!isPro) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const cleanHostname = inputDomain.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
      
      if (!cleanHostname.includes('.')) {
        throw new Error("Please enter a valid domain name (e.g., domain.com).");
      }

      // The server checks restrictions, adds the Netlify alias and saves the domain.
      const { domain: created } = await api.post('domain', { hostname: cleanHostname });
      setDomain(created);
      setStatusMessage({ type: 'success', text: 'Domain connected! Now update your DNS settings.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshStatus = async () => {
    if (!domain) return;
    setLoading(true);
    setStatusMessage(null);
    try {
      const { domain: refreshed, status } = await api.post('domain/refresh');
      setDomain(refreshed);

      if (status.dns && status.ssl) {
        setStatusMessage({ type: 'success', text: 'DNS and SSL are fully verified!' });
      } else if (status.dns) {
        setStatusMessage({ type: 'info', text: 'DNS verified! SSL is still propagating.' });
      } else {
        setStatusMessage({ type: 'info', text: 'DNS not yet detected. This can take up to 24 hours.' });
      }
    } catch (err) {
      setError("Failed to check status. Try again later.");
    } finally {
      setLoading(false);
      setTimeout(() => setStatusMessage(null), 5000);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to disconnect this domain? This will also remove the domain from our hosting infrastructure.')) return;
    setLoading(true);
    try {
      await api.del('domain');
      setDomain(null);
      setStatusMessage({ type: 'info', text: 'Domain disconnected and removed from server.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !domain) return <div className="p-12 text-center animate-pulse text-gray-400">Loading domain settings...</div>;

  return (
    <div className="max-w-4xl space-y-8 animate-in fade-in slide-in-from-bottom-2">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Custom Domain</h2>
          <p className="text-gray-500 mt-1">Point your own domain to your TapBadge profile.</p>
        </div>
        {domain && (
          <button onClick={handleDelete} className="text-xs font-bold text-red-400 hover:text-red-600 flex items-center gap-1">
            <SafeIcon name="Trash2" size={14} /> Remove Domain
          </button>
        )}
      </div>

      <AnimatePresence>
        {statusMessage && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
            className={`p-4 rounded-2xl text-sm font-bold border flex items-center gap-3 ${statusMessage.type === 'success' ? 'bg-green-50 border-green-100 text-green-700' : 'bg-indigo-50 border-indigo-100 text-indigo-700'}`}
          >
            <SafeIcon name={statusMessage.type === 'success' ? "CheckCircle" : "Info"} />
            {statusMessage.text}
          </motion.div>
        )}
      </AnimatePresence>

      {!isPro ? (
        <div className="bg-indigo-50 border border-indigo-100 rounded-[2.5rem] p-10 text-center">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-indigo-600 mx-auto mb-6 shadow-sm">
            <SafeIcon name="Globe" size={32} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Feature Only</h3>
          <p className="text-gray-600 mb-8 max-w-sm mx-auto">
            Upgrade to Pro to use your own domain like <b>johnsmith.com</b> for your digital card.
          </p>
          <Link to="/pricing">
            <Button>View Pro Plans</Button>
          </Link>
        </div>
      ) : domain ? (
        <div className="space-y-6">
          <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${domain.verification_status === 'active' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                  <SafeIcon name={domain.verification_status === 'active' ? "CheckCircle" : "Clock"} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{domain.hostname}</h3>
                  <p className="text-sm text-gray-400">
                    Status: <span className="capitalize font-bold text-indigo-600">{domain.verification_status}</span>
                  </p>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={refreshStatus} disabled={loading}>
                <SafeIcon name="RefreshCw" className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Checking...' : 'Check DNS Status'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <StatusBadge label="DNS Records" active={domain.verification_status === 'active'} />
              <StatusBadge label="SSL Certificate" active={domain.ssl_status === 'active'} />
            </div>

            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">DNS Configuration</h4>
              <p className="text-sm text-gray-600 mb-4">Add the following record to your domain provider (GoDaddy, Namecheap, etc.):</p>
              <div className="space-y-3">
                <DnsRow label="Type" value="CNAME" />
                <DnsRow label="Host" value="@ or your subdomain" />
                <DnsRow label="Points To" value="connect.tapbadge.net" />
              </div>
              <p className="text-[10px] text-gray-400 mt-4 italic text-center">Note: Propagation can take up to 24-48 hours.</p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleAddDomain} className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="max-w-md mx-auto py-8 text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 mx-auto mb-6">
              <SafeIcon name="Plus" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Connect Your Domain</h3>
            <p className="text-gray-500 text-sm mb-8">Enter the domain you'd like to use for your profile.</p>
            <div className="space-y-4">
              <input 
                required 
                type="text" 
                placeholder="e.g. card.johnsmith.com" 
                value={inputDomain} 
                onChange={(e) => setInputDomain(e.target.value)}
                className="w-full px-6 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none text-center font-bold text-lg" 
              />
              {error && <p className="text-red-500 text-xs font-medium">{error}</p>}
              <Button type="submit" className="w-full py-4 rounded-2xl shadow-lg shadow-indigo-100" disabled={isSubmitting}>
                {isSubmitting ? 'Connecting...' : 'Connect Domain'}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

function StatusBadge({ label, active }) {
  return (
    <div className={`p-4 rounded-2xl border flex items-center justify-between transition-all ${active ? 'bg-green-50 border-green-100 text-green-700' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      <SafeIcon name={active ? "Check" : "AlertCircle"} size={16} />
    </div>
  );
}

function DnsRow({ label, value }) {
  return (
    <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-gray-200">
      <span className="text-xs font-bold text-gray-400 uppercase">{label}</span>
      <span className="text-sm font-mono font-bold text-indigo-600">{value}</span>
    </div>
  );
}
